import { useCallback, useEffect, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, useTokens } from '@vibesites/runtime/react';
import { EditableSlotGroup, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastSafeTextColor } from '../utils/wcagContrast';

const SLIDE_INTERVAL = 5000;

const officePhotoDef: SlotGroupDefinition = {
  label: 'Office Nook Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[47].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[47].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 47,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const bedroomPhotoDef: SlotGroupDefinition = {
  label: 'Bedroom Workspace Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[12].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[12].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 12,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const livingPhotoDef: SlotGroupDefinition = {
  label: 'Living Room Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[18].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[18].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 18,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const diningPhotoDef: SlotGroupDefinition = {
  label: 'Dining Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[22].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[22].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 22,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const kitchenPhotoDef: SlotGroupDefinition = {
  label: 'Kitchen Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[23].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[23].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 23,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const balconyPhotoDef: SlotGroupDefinition = {
  label: 'Balcony Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[16].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[16].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 16,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function LiveWorkUnits() {
  useRevealStyles();
  const bandRef = useScrollReveal<HTMLDivElement>();
  const tokens = useTokens();
  const linkColor = contrastSafeTextColor(tokens.color.primary, tokens.color.background);

  const kicker = useContentToken('liveworkunits.kicker');
  const heading = useContentToken('liveworkunits.heading');
  const office = useSlotGroup('liveworkunits.officePhoto', officePhotoDef);
  const bedroom = useSlotGroup('liveworkunits.bedroomPhoto', bedroomPhotoDef);
  const living = useSlotGroup('liveworkunits.livingPhoto', livingPhotoDef);
  const dining = useSlotGroup('liveworkunits.diningPhoto', diningPhotoDef);
  const kitchen = useSlotGroup('liveworkunits.kitchenPhoto', kitchenPhotoDef);
  const balcony = useSlotGroup('liveworkunits.balconyPhoto', balconyPhotoDef);
  const slides = [office, bedroom, living, dining, kitchen, balcony].filter((slide, index, all) => {
    if (!slide.image) return false;
    return all.findIndex((other) => other.image === slide.image) === index;
  });

  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = slides.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent((prev) => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    setCurrent((prev) => (count === 0 ? 0 : prev % count));
  }, [count]);

  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReducedMotion(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const rotating = count > 1 && !paused && !reducedMotion;

  useEffect(() => {
    if (!rotating) return;
    const timer = window.setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => window.clearInterval(timer);
  }, [rotating, advance]);

  return (
    <section className="bg-background py-14 md:py-20 lg:py-24">
      <div ref={bandRef} className="live-work reveal-fade-up mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="live-work-grid">
          <div
            className="live-work-carousel"
            role="region"
            aria-roledescription="carousel"
            aria-label="Live-work unit photos"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
            }}
          >
            <div aria-live={rotating ? 'off' : 'polite'}>
              {slides.map((slide, index) => (
                <div
                  key={slide.image || index}
                  className={`live-work-slide${current === index ? ' live-work-slide--active' : ''}`}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${index + 1} of ${count}`}
                  aria-hidden={current !== index}
                >
                  {index === 0 && (
                    <EditableSlotGroup groupPath="liveworkunits.officePhoto" definition={officePhotoDef}>
                      <VibesitesImage url={office.image || ''} alt={office.alt || 'Home office nook'} className="h-full w-full object-cover" sizes="(min-width: 992px) 58vw, 100vw" priority />
                    </EditableSlotGroup>
                  )}
                  {index === 1 && (
                    <EditableSlotGroup groupPath="liveworkunits.bedroomPhoto" definition={bedroomPhotoDef}>
                      <VibesitesImage url={bedroom.image || ''} alt={bedroom.alt || 'Bedroom with a workspace'} className="h-full w-full object-cover" sizes="(min-width: 992px) 58vw, 100vw" />
                    </EditableSlotGroup>
                  )}
                  {index === 2 && (
                    <EditableSlotGroup groupPath="liveworkunits.livingPhoto" definition={livingPhotoDef}>
                      <VibesitesImage url={living.image || ''} alt={living.alt || 'Living room'} className="h-full w-full object-cover" sizes="(min-width: 992px) 58vw, 100vw" />
                    </EditableSlotGroup>
                  )}
                  {index === 3 && (
                    <EditableSlotGroup groupPath="liveworkunits.diningPhoto" definition={diningPhotoDef}>
                      <VibesitesImage url={dining.image || ''} alt={dining.alt || 'Dining area'} className="h-full w-full object-cover" sizes="(min-width: 992px) 58vw, 100vw" />
                    </EditableSlotGroup>
                  )}
                  {index === 4 && (
                    <EditableSlotGroup groupPath="liveworkunits.kitchenPhoto" definition={kitchenPhotoDef}>
                      <VibesitesImage url={kitchen.image || ''} alt={kitchen.alt || 'Kitchen'} className="h-full w-full object-cover" sizes="(min-width: 992px) 58vw, 100vw" />
                    </EditableSlotGroup>
                  )}
                  {index === 5 && (
                    <EditableSlotGroup groupPath="liveworkunits.balconyPhoto" definition={balconyPhotoDef}>
                      <VibesitesImage url={balcony.image || ''} alt={balcony.alt || 'Private balcony'} className="h-full w-full object-cover" sizes="(min-width: 992px) 58vw, 100vw" />
                    </EditableSlotGroup>
                  )}
                </div>
              ))}
            </div>

            {count > 1 && (
              <>
                <button type="button" className="live-work-arrow live-work-arrow--prev" onClick={() => advance(-1)} aria-label="Previous photo">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <button type="button" className="live-work-arrow live-work-arrow--next" onClick={() => advance(1)} aria-label="Next photo">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
                <div className="live-work-dots">
                  {slides.map((slide, index) => (
                    <button
                      key={slide.image || index}
                      type="button"
                      className={`live-work-dot${index === current ? ' live-work-dot--active' : ''}`}
                      onClick={() => setCurrent(index)}
                      aria-label={`Photo ${index + 1} of ${count}`}
                      aria-current={index === current ? 'true' : undefined}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="live-work-copy">
            <EditableToken path="liveworkunits.kicker" as="p" className="live-work-kicker">
              {kicker || 'Designed for It All'}
            </EditableToken>
            <EditableToken path="liveworkunits.heading" as="h2" className="live-work-heading">
              {heading || 'Live-Work Units'}
            </EditableToken>
            <RichTextContent
              path="liveworkunits.body"
              as="div"
              className="live-work-body"
              style={linkColor ? { ['--live-work-link-color' as string]: linkColor } : undefined}
            />
          </div>
        </div>
      </div>

      <style>{`
        .live-work-grid {
          display: grid;
          align-items: start;
          gap: 1.75rem;
        }
        .live-work-kicker {
          margin: 0 0 0.45rem;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
        }
        .live-work-heading {
          margin: 0 0 1.25rem;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.75rem, 2.4vw, 2.5rem);
          font-weight: 400;
          line-height: 1.15;
          color: var(--vibe-color-text);
        }
        .live-work-body {
          font-family: var(--vibe-font-body);
          font-size: 0.98rem;
          line-height: 1.55;
          color: var(--vibe-color-text);
        }
        .live-work-body p + p,
        .live-work-body ul + p,
        .live-work-body p + ul {
          margin-top: 1rem;
        }
        .live-work-body a {
          color: var(--live-work-link-color, var(--vibe-color-primary));
          text-decoration: underline;
        }
        .live-work-body ul {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .live-work-body li {
          display: flex;
          align-items: flex-start;
          gap: 0.7rem;
          padding: 0.4rem 0;
          border-bottom: 1px solid var(--vibe-color-border);
        }
        .live-work-body li::before {
          content: '';
          flex-shrink: 0;
          width: 6px;
          height: 6px;
          margin-top: 0.5em;
          border-radius: 50%;
          background: var(--vibe-color-primary);
        }

        .live-work-carousel {
          position: relative;
          min-height: 20rem;
          height: 70vw;
          max-height: 36rem;
          overflow: hidden;
          background: var(--vibe-color-surface);
        }
        .live-work-slide {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .live-work-slide--active {
          opacity: 1;
          z-index: 1;
          pointer-events: auto;
        }
        .live-work-slide > * {
          display: block;
          width: 100%;
          height: 100%;
        }
        .live-work-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 44px;
          height: 44px;
          border: 1px solid var(--vibe-color-border);
          background: var(--vibe-color-background);
          color: var(--vibe-color-text);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 2;
          box-shadow: var(--vibe-shadow-md, 0 4px 16px rgba(0, 0, 0, 0.12));
        }
        .live-work-arrow:hover {
          color: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
        }
        .live-work-arrow:focus-visible,
        .live-work-dot:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 2px;
        }
        .live-work-arrow--prev { left: 12px; }
        .live-work-arrow--next { right: 12px; }
        .live-work-dots {
          position: absolute;
          bottom: 14px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 2;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 8px;
          max-width: calc(100% - 7rem);
        }
        .live-work-dot {
          width: 8px;
          height: 8px;
          padding: 0;
          border-radius: 50%;
          border: 1.5px solid var(--vibe-color-background);
          background: transparent;
          cursor: pointer;
          box-shadow: 0 0 0 1px color-mix(in srgb, var(--vibe-color-text) 35%, transparent);
        }
        .live-work-dot--active {
          background: var(--vibe-color-background);
        }

        @media (min-width: 992px) {
          .live-work-grid {
            grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr);
            gap: 3.5rem;
            align-items: stretch;
          }
          .live-work-carousel {
            height: 100%;
            min-height: 30rem;
            max-height: none;
          }
        }
        @media (min-width: 1200px) {
          .live-work-grid {
            gap: 4.5rem;
          }
        }
        @media (max-width: 991px) {
          .live-work-carousel {
            order: -1;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .live-work-slide {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'LiveWorkUnits',
  displayName: 'Live/Work Units',
  tokens: {
    'liveworkunits.kicker': {
      label: 'Kicker',
      type: 'text',
      defaultValue: 'Designed for It All',
    },
    'liveworkunits.heading': {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Live-Work Units',
    },
    'liveworkunits.body': {
      label: 'Copy',
      type: 'richtext',
      defaultValue:
        '<p>When a simple home office isn\'t quite enough, and you\'re seeking more space and flexibility with all of the comforts of our upscale apartments, check out our versatile live-work units. A great option for creatives, small business owners, and remote professionals, these floor plans allow you to transition from couch to studio or conference call in a heartbeat. Benefits of our live-work floor plans include:</p><ul><li>No Commute</li><li>Enhanced Work-Life Balance</li><li>Adaptable Workspaces</li></ul><p><a href="/schedule-a-tour">Schedule a tour</a> of <span data-type="field-token" data-field="property_name"></span> today and see if our unique floor plans are the right fit for your lifestyle.</p>',
    },
  },
  slotGroups: {
    'liveworkunits.officePhoto': officePhotoDef,
    'liveworkunits.bedroomPhoto': bedroomPhotoDef,
    'liveworkunits.livingPhoto': livingPhotoDef,
    'liveworkunits.diningPhoto': diningPhotoDef,
    'liveworkunits.kitchenPhoto': kitchenPhotoDef,
    'liveworkunits.balconyPhoto': balconyPhotoDef,
  },
};

(LiveWorkUnits as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default LiveWorkUnits;
